import React, { useRef, useState, useEffect } from 'react';
import PillDropdown from '../../../../components/PillDropdown';
import userIcon from '../../../../imgs/user.png';
import { getKeyboardShortcutDisplay } from '../projectConstants';

export default function BreakdownForm({
  showBreakdownOptions,
  setShowBreakdownOptions,
  modalChild,
  breakdownNewStoryName,
  setBreakdownNewStoryName,
  breakdownTaskEstimate,
  setBreakdownTaskEstimate,
  breakdownTaskStatus,
  setBreakdownTaskStatus,
  breakdownTaskAssignee,
  setBreakdownTaskAssignee,
  breakdownSelectedStoryId,
  setBreakdownSelectedStoryId,
  breakdownStories,
  storyInputValue,
  setStoryInputValue,
  noStorySelected,
  setNoStorySelected,
  users,
  handleCreateNewTask,
  breakdownOptionsRef,
  hasEstimateInList,
  hasStatusInList,
  hasAssigneeInList,
  hasStoryInList
}) {
  const breakdownTaskTitleRef = useRef(null);
  const breakdownEstimateRef = useRef(null);
  const breakdownStatusRef = useRef(null);
  const breakdownAssigneeRef = useRef(null);
  const breakdownStoryInputRef = useRef(null);
  const [breakdownTitleWidth, setBreakdownTitleWidth] = useState(100);

  // Helper function to cancel task creation
  // Only cancels if task has no title
  const cancelTaskCreation = () => {
    // If task has a title, don't cancel - create the task instead
    if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
      handleCreateNewTask();
      return;
    }
    
    setShowBreakdownOptions(false);
    setBreakdownNewStoryName("");
    setBreakdownTitleWidth(100);
    setStoryInputValue("");
    setNoStorySelected(false);
    setBreakdownSelectedStoryId(null);
    setBreakdownTaskEstimate(null);
    setBreakdownTaskAssignee(null);
    setBreakdownTaskStatus('TODO');
  };

  // Get text width for auto-sizing breakdown title
  const getBreakdownTextWidth = (text) => {
    if (!breakdownTaskTitleRef.current) return 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const computedStyle = window.getComputedStyle(breakdownTaskTitleRef.current);
    context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    return context.measureText(text || '').width;
  };

  useEffect(() => {
    if (showBreakdownOptions && breakdownTaskTitleRef.current) {
      breakdownTaskTitleRef.current.focus();
    }
  }, [showBreakdownOptions]);

  if (!showBreakdownOptions) {
    return (
      <button
        data-breakdown-form="add-work-button"
        style={{
          padding: '6px 12px',
          height: 28,
          borderRadius: 4,
          border: '1px solid #ddd',
          background: '#fff',
          color: '#111',
          fontSize: 12,
          fontWeight: 'normal',
          cursor: 'pointer',
          marginBottom: 3,
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}
        onClick={async () => {
          setShowBreakdownOptions(true);
          setBreakdownNewStoryName("");
          setBreakdownTitleWidth(100);
          setStoryInputValue("");
          setNoStorySelected(false);
          setBreakdownSelectedStoryId(null);
          setBreakdownTaskEstimate(null);
          setBreakdownTaskAssignee(null);
          setBreakdownTaskStatus('TODO');
        }}
      >
        <span>Add Work</span>
        <span style={{ 
          fontSize: 11, 
          color: '#666',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {getKeyboardShortcutDisplay()} + ⏎
        </span>
      </button>
    );
  }

  return (
    <div 
      ref={breakdownOptionsRef}
      data-breakdown-form="inline-creation"
      style={{
        position: "relative",
        background: '#e0e0e0',
        borderRadius: 4,
        padding: '0px 12px',
        minWidth: 100,
        height: 30,
        boxSizing: 'border-box',
        fontSize: 12,
        color: '#111',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'default',
        marginBottom: 3,
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          cancelTaskCreation();
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && e.target.tagName !== 'INPUT') {
          e.preventDefault();
          e.stopPropagation();
          handleCreateNewTask();
        }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();
          handleCreateNewTask();
        }
      }}
      tabIndex={0}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, justifyContent: 'space-between' }}>
        {/* Text field for task name - on the left, auto-sized */}
        <input
          ref={breakdownTaskTitleRef}
          type="text"
          value={breakdownNewStoryName}
          onChange={(e) => {
            setBreakdownNewStoryName(e.target.value);
            const textWidth = getBreakdownTextWidth(e.target.value);
            setBreakdownTitleWidth(Math.max(50, textWidth + 10));
          }}
          placeholder={`A task to be done for ${modalChild?.title || 'this sub-epic'}`}
          autoFocus
          style={{ 
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 12,
            color: '#111',
            fontWeight: 'normal', 
            padding: 0,
            minWidth: 50,
            width: breakdownNewStoryName.trim() ? `${breakdownTitleWidth}px` : 280,
            cursor: 'text',
          }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            // If task has a title, create it instead of canceling
            if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
              handleCreateNewTask();
              return;
            }
            // Cancel task creation - reset all fields and hide the form
            setShowBreakdownOptions(false);
            setBreakdownNewStoryName("");
            setBreakdownTitleWidth(100);
            setStoryInputValue("");
            setNoStorySelected(false);
            setBreakdownSelectedStoryId(null);
            setBreakdownTaskEstimate(null);
            setBreakdownTaskAssignee(null);
            setBreakdownTaskStatus('TODO');
            return;
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.stopPropagation();
            handleCreateNewTask();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            if (breakdownEstimateRef.current && breakdownEstimateRef.current.focus) {
              breakdownEstimateRef.current.focus();
              const input = breakdownEstimateRef.current.getRef?.();
              if (input) {
                input.focus();
              }
            }
          } else if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            if (breakdownEstimateRef.current && breakdownEstimateRef.current.focus) {
              breakdownEstimateRef.current.focus();
              const input = breakdownEstimateRef.current.getRef?.();
              if (input) {
                input.focus();
              }
            }
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
        {/* Fields container - always show all fields for new task, right-aligned */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: (hasEstimateInList || hasStatusInList || hasAssigneeInList || hasStoryInList) ? 'auto' : 0, position: 'relative' }}>
        {/* Estimate - reserve space if any task has it, show component if populated or always for new task */}
        {(hasEstimateInList || true) && (
          <div 
            className="editable-field"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ display: "flex", justifyContent: 'flex-start', marginRight: 6 }}
          >
            <PillDropdown
            ref={breakdownEstimateRef}
            hasPrefixNumber={true}
            prefixNumber={
              breakdownTaskEstimate
                ? breakdownTaskEstimate <= 8 || breakdownTaskEstimate % 8 !== 0
                  ? breakdownTaskEstimate
                  : breakdownTaskEstimate / 8
                : null
            }
            list={[{ value: "days", id: "days" }, { value: "hrs", id: "hrs" }]}
            selectedId={breakdownTaskEstimate ? (breakdownTaskEstimate % 8 === 0 ? "days" : "hrs") : null}
            placeholder="Estimate"
            onSelect={(item, numberValue) => {
              const number = numberValue || (breakdownTaskEstimate && (breakdownTaskEstimate <= 8 || breakdownTaskEstimate % 8 !== 0)
                ? breakdownTaskEstimate
                : breakdownTaskEstimate
                ? breakdownTaskEstimate / 8
                : 0) || 0;
              const _estimate = item.id === "hrs" ? number : number * 8;
              setBreakdownTaskEstimate(_estimate);
            }}
            onClear={() => {
              setBreakdownTaskEstimate(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                // If task has a title, create it instead of canceling
                if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
                  handleCreateNewTask();
                  return;
                }
                // Cancel task creation - reset all fields and hide the form
                setShowBreakdownOptions(false);
                setBreakdownNewStoryName("");
                setBreakdownTitleWidth(100);
                setStoryInputValue("");
                setNoStorySelected(false);
                setBreakdownSelectedStoryId(null);
                setBreakdownTaskEstimate(null);
                setBreakdownTaskAssignee(null);
                setBreakdownTaskStatus('TODO');
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                if (!e.defaultPrevented && breakdownStatusRef.current?.focus) {
                  e.preventDefault();
                  e.stopPropagation();
                  setTimeout(() => {
                    breakdownStatusRef.current.focus();
                    const statusInput = breakdownStatusRef.current.getRef?.();
                    if (statusInput) {
                      statusInput.focus();
                      statusInput.click();
                    }
                  }, 0);
                }
              }
            }}
            pillStyle={{
              background: "transparent",
              color: "gray",
              borderRadius: 4,
              fontSize: 10,
              padding: "1px 0px",
              minWidth: 50,
              textAlign: 'right',
            }}
          />
          </div>
        )}
        
        {/* Status - reserve space if any task has it, show component if populated or always for new task */}
        {(hasStatusInList || true) && (
          <div 
            className="editable-field"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ display: "flex", justifyContent: 'flex-end', flexShrink: 0 }}
          >
            <PillDropdown
            ref={breakdownStatusRef}
            list={[
              { value: 'TODO', id: 'TODO' },
              { value: 'IN PROGRESS', id: 'IN_PROGRESS' },
              { value: 'REVIEW', id: 'REVIEW' },
              { value: 'DONE', id: 'DONE' },
            ]}
            selectedId={breakdownTaskStatus || 'TODO'}
            placeholder="Status"
            onSelect={(item) => {
              setBreakdownTaskStatus(item.id);
            }}
            onClear={() => {
              setBreakdownTaskStatus('TODO');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                // If task has a title, create it instead of canceling
                if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
                  handleCreateNewTask();
                  return;
                }
                // Cancel task creation - reset all fields and hide the form
                setShowBreakdownOptions(false);
                setBreakdownNewStoryName("");
                setBreakdownTitleWidth(100);
                setStoryInputValue("");
                setNoStorySelected(false);
                setBreakdownSelectedStoryId(null);
                setBreakdownTaskEstimate(null);
                setBreakdownTaskAssignee(null);
                setBreakdownTaskStatus('TODO');
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                if (!e.defaultPrevented && breakdownAssigneeRef.current?.focus) {
                  e.preventDefault();
                  e.stopPropagation();
                  setTimeout(() => {
                    breakdownAssigneeRef.current.focus();
                    const assigneeInput = breakdownAssigneeRef.current.getRef?.();
                    if (assigneeInput) {
                      assigneeInput.focus();
                      assigneeInput.click();
                    }
                  }, 0);
                }
              }
            }}
            pillStyle={{
              background: breakdownTaskStatus === 'DONE' ? '#4CAF50' : 
                         breakdownTaskStatus === 'IN_PROGRESS' ? '#FF9800' : 
                         breakdownTaskStatus === 'REVIEW' ? '#2196F3' : '#9E9E9E',
              color: 'white',
              fontSize: 9,
              borderRadius: 2,
              minWidth: 0,
              padding: "3px 2px 3px 2px",
            }}
          />
          </div>
        )}
        
        {/* Assignee - reserve space if any task has it, show component if populated or always for new task */}
        {(hasAssigneeInList || true) && (
          <div style={{ position: 'relative', width: 20, height: 20, margin: '-6px 0px -6px 0px', flexShrink: 0 }}>
          {breakdownTaskAssignee && (() => {
            const assigneeUser = users.find(u => u.id === breakdownTaskAssignee);
            return assigneeUser ? (
              <div 
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: assigneeUser.avatar ? 'transparent' : '#999',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 7,
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  cursor: 'pointer',
                  zIndex: 2,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (breakdownAssigneeRef.current && breakdownAssigneeRef.current.focus) {
                    breakdownAssigneeRef.current.focus();
                  }
                }}
              >
                {assigneeUser.avatar ? (
                  <img 
                    src={assigneeUser.avatar} 
                    alt={`${assigneeUser.firstname} ${assigneeUser.lastname || ''}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.backgroundColor = '#999';
                    }}
                  />
                ) : null}
                {!assigneeUser.avatar && (
                  <span>
                    {assigneeUser.firstname ? assigneeUser.firstname.substring(0, 1).toUpperCase() + assigneeUser.firstname.substring(1, 2).toLowerCase() : ''}{assigneeUser.lastname ? assigneeUser.lastname.substring(0, 1).toUpperCase() : ''}
                  </span>
                )}
              </div>
            ) : null;
          })()}
          {!breakdownTaskAssignee && (
            <div 
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: '1px solid #bbb',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                cursor: 'pointer',
                zIndex: 2,
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (breakdownAssigneeRef.current && breakdownAssigneeRef.current.focus) {
                  breakdownAssigneeRef.current.focus();
                }
              }}
            >
              <img 
                src={userIcon} 
                alt="User"
                style={{
                  width: 6,
                  height: 8,
                  opacity: 0.2,
                }}
              />
            </div>
          )}
          <div 
            className="editable-field assignee-pill"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ 
              position: breakdownTaskAssignee ? 'absolute' : 'relative',
              top: breakdownTaskAssignee ? 0 : 'auto',
              left: breakdownTaskAssignee ? 0 : 'auto',
              width: 20,
              height: 20,
            }}
          >
            <PillDropdown
              ref={breakdownAssigneeRef}
              list={(users || []).map((val) => ({
                value: !val.lastname ? val.firstname : val.firstname + " " + val.lastname.charAt(0),
                id: val.id,
              }))}
              selectedId={breakdownTaskAssignee || null}
              placeholder=""
              onSelect={(item) => {
                setBreakdownTaskAssignee(item.id);
              }}
              onClear={() => {
                setBreakdownTaskAssignee(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  // Cancel task creation - reset all fields and hide the form
                  setShowBreakdownOptions(false);
                  setBreakdownNewStoryName("");
                  setBreakdownTitleWidth(100);
                  setStoryInputValue("");
                  setNoStorySelected(false);
                  setBreakdownSelectedStoryId(null);
                  setBreakdownTaskEstimate(null);
                  setBreakdownTaskAssignee(null);
                  setBreakdownTaskStatus('TODO');
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                  if (!e.defaultPrevented && breakdownStoryInputRef.current) {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimeout(() => {
                      if (breakdownStoryInputRef.current) {
                        breakdownStoryInputRef.current.focus();
                      }
                    }, 0);
                  }
                }
              }}
              pillStyle={{
                background: 'transparent',
                color: 'transparent',
                border: 'none',
                borderRadius: '50%',
                fontSize: 0,
                padding: 0,
                minWidth: 20,
                width: 20,
                height: 20,
              }}
            />
          </div>
        </div>
        )}
        
        {/* Story - reserve space if any task has it, show component if populated or always for new task */}
        {(hasStoryInList || true) && (
          <div 
            className="editable-field story-field"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ position: 'relative', width: 60, height: 16 }}
            onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              // Cancel task creation - reset all fields and hide the form
              setShowBreakdownOptions(false);
              setBreakdownNewStoryName("");
              setBreakdownTitleWidth(100);
              setStoryInputValue("");
              setNoStorySelected(false);
              setBreakdownSelectedStoryId(null);
              setBreakdownTaskEstimate(null);
              setBreakdownTaskAssignee(null);
              setBreakdownTaskStatus('TODO');
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
              if (breakdownSelectedStoryId || breakdownSelectedStoryId === -1 || noStorySelected || breakdownNewStoryName.trim()) {
                e.preventDefault();
                e.stopPropagation();
                handleCreateNewTask();
              }
            }
          }}
        >
          <PillDropdown
            ref={breakdownStoryInputRef}
            list={(() => {
              const storyList = (breakdownStories || []).filter(story => story.status !== 'COMPLETE').map((story) => ({
                value: story.title || 'Untitled Story',
                id: story.id,
              }));
              
              // Always show "No story" at the top
              const listWithNoStory = [
                { value: 'No story', id: -1 },
                ...storyList
              ];
              
              // If user is typing and the text doesn't match any existing story, add "Create new:" option
              if (storyInputValue.trim()) {
                const searchLower = storyInputValue.toLowerCase().trim();
                const hasMatch = storyList.some(story => 
                  story.value && story.value.toLowerCase().includes(searchLower)
                );
                
                if (!hasMatch) {
                  return [
                    { value: 'No story', id: -1 },
                    { value: `Create new: "${storyInputValue.trim()}"`, id: '__create_new__' },
                    ...storyList
                  ];
                }
              }
              
              return listWithNoStory;
            })()}
            selectedId={breakdownSelectedStoryId === -1 ? -1 : (breakdownSelectedStoryId || null)}
            value={breakdownSelectedStoryId === -1 || noStorySelected
              ? "No story"
              : breakdownSelectedStoryId 
                ? (breakdownStories.find(s => s.id === breakdownSelectedStoryId)?.title || 'Untitled Story')
                : storyInputValue}
            placeholder="Story..."
            onSelect={(item) => {
              if (item.id === -1) {
                setBreakdownSelectedStoryId(-1);
                setNoStorySelected(true);
                setStoryInputValue("");
              } else if (item.id === '__create_new__') {
                // This will be handled by handleCreateNewTask which creates the story
                // Just set the state to indicate we want to create a new story
                setBreakdownSelectedStoryId(null);
                setNoStorySelected(false);
                // Don't clear storyInputValue - it will be used to create the story
              } else {
                setBreakdownSelectedStoryId(item.id);
                setNoStorySelected(false);
                setStoryInputValue("");
              }
              // Auto-create task after selecting a story if title is filled
              if (breakdownNewStoryName.trim()) {
                setTimeout(() => {
                  handleCreateNewTask();
                }, 100);
              }
            }}
            onChange={(e) => {
              setStoryInputValue(e.target.value);
              if (e.target.value.trim() === '' || e.target.value.trim().toLowerCase() === 'no story') {
                setBreakdownSelectedStoryId(null);
                setNoStorySelected(false);
              }
            }}
            onKeyDown={(e) => {
              // If there's input text, let PillDropdown handle Enter to select highlighted item
              if (e.key === 'Enter' && storyInputValue.trim()) {
                // Don't interfere - let PillDropdown handle the selection
                return;
              }
              // Otherwise, let the parent handler deal with it (creating task, etc.)
            }}
            left={false}
            customStyle={{
              width: "100%",
              height: "16px",
              lineHeight: "16px",
              padding: "1px 0px",
              textAlign: "right",
            }}
          />
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

